/**
 * Client Management Actions Component
 * Provides role-based action menus for client management
 * - Admin: Full access (view, edit, disable/enable, reset password, delete)
 * - Staff: Limited access (view, edit, contact actions)
 */

import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import { canPerformAction, isAdmin } from '../../utils/permissions';

const DISABLE_USER = gql`
  mutation DisableUser($userId: ID!) {
    disableUser(userId: $userId) {
      id
      email
      isEnabled
      loginDisabled
    }
  }
`;

const ENABLE_USER = gql`
  mutation EnableUser($userId: ID!) {
    enableUser(userId: $userId) {
      id
      email
      isEnabled
      loginDisabled
    }
  }
`;

const DISABLE_USER_LOGIN = gql`
  mutation DisableUserLogin($userId: ID!) {
    disableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const ENABLE_USER_LOGIN = gql`
  mutation EnableUserLogin($userId: ID!) {
    enableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($userId: ID!) {
    resetUserPassword(userId: $userId) {
      success
      message
      temporaryPassword
    }
  }
`;

// Type definitions for Apollo mutations
interface ResetUserPasswordData {
  resetUserPassword: {
    success: boolean;
    message?: string;
    temporaryPassword?: string;
  };
}

interface ClientManagementActionsProps {
  clientId: string;
  clientName: string;
  clientEmail: string;
  isEnabled?: boolean;
  loginDisabled?: boolean;
  userRole?: string;
  onView?: () => void;
  onEdit?: () => void;
  onRefresh?: () => void;
}

export function ClientManagementActions({
  clientId,
  clientName,
  clientEmail,
  isEnabled = true,
  loginDisabled = false,
  userRole,
  onView,
  onEdit,
  onRefresh,
}: ClientManagementActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const buttonRef = useRef<HTMLDivElement>(null);

  const [disableUser, { loading: disabling }] = useMutation(DISABLE_USER);
  const [enableUser, { loading: enabling }] = useMutation(ENABLE_USER);
  const [disableUserLogin, { loading: disablingLogin }] = useMutation(DISABLE_USER_LOGIN);
  const [enableUserLogin, { loading: enablingLogin }] = useMutation(ENABLE_USER_LOGIN);
  const [resetPassword, { loading: resetting }] = useMutation<ResetUserPasswordData>(RESET_USER_PASSWORD);

  const isAdminUser = isAdmin(userRole as any);

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If less than 250px space below and more space above, open upward
      if (spaceBelow < 250 && spaceAbove > spaceBelow) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [showMenu]);

  const handleDisableUser = async () => {
    if (!confirm(`Are you sure you want to restrict access for ${clientName}? They will see a read-only screen but can still log in.`)) {
      return;
    }

    try {
      await disableUser({ variables: { userId: clientId } });
      alert(`${clientName}'s access has been restricted.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error disabling user:', error);
      alert(`Error restricting access: ${error.message}`);
    }
  };

  const handleEnableUser = async () => {
    try {
      await enableUser({ variables: { userId: clientId } });
      alert(`${clientName}'s access has been restored.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error enabling user:', error);
      alert(`Error restoring access: ${error.message}`);
    }
  };

  const handleBlockLogin = async () => {
    if (!confirm(`Are you sure you want to block login for ${clientName}? They will NOT be able to sign in at all.`)) {
      return;
    }

    try {
      await disableUserLogin({ variables: { userId: clientId } });
      alert(`${clientName}'s login has been blocked.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error blocking user login:', error);
      alert(`Error blocking login: ${error.message}`);
    }
  };

  const handleEnableLogin = async () => {
    try {
      await enableUserLogin({ variables: { userId: clientId } });
      alert(`${clientName}'s login has been enabled.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error enabling user login:', error);
      alert(`Error enabling login: ${error.message}`);
    }
  };

  const handleResetPassword = async () => {
    if (!confirm(`Reset password for ${clientName}? A temporary password will be generated and must be shared with the client securely.`)) {
      return;
    }

    try {
      const result = await resetPassword({ variables: { userId: clientId } });
      const tempPassword = result.data?.resetUserPassword?.temporaryPassword;
      
      if (tempPassword) {
        setTemporaryPassword(tempPassword);
        setShowPasswordModal(true);
      } else {
        alert('Password reset initiated. The client will receive an email with instructions.');
      }
      
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      alert(`Error resetting password: ${error.message}`);
    }
  };

  const handleCopyPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      alert('Temporary password copied to clipboard!');
    }
  };

  const handleSendEmail = () => {
    if (temporaryPassword) {
      const subject = encodeURIComponent('Your AALaw Account - Temporary Password');
      const body = encodeURIComponent(
        `Hello ${clientName},\n\n` +
        `Your account password has been reset. Please use the following temporary password to sign in:\n\n` +
        `Temporary Password: ${temporaryPassword}\n\n` +
        `You will be required to change this password upon your next login.\n\n` +
        `Best regards,\nAA Law Team`
      );
      window.location.href = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
    }
  };

  return (
    <>
      <div className="relative" ref={buttonRef}>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="text-gray-600 hover:text-aalaw-red"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </Button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 ${
              menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
            }`}>
              <div className="py-1">
                {/* View Details */}
                {onView && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                )}

                {/* Edit - Available to staff with update permission */}
                {onEdit && canPerformAction(userRole as any, 'clients', 'update') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Client
                  </button>
                )}

                {/* Contact Actions - Available to all staff */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `mailto:${clientEmail}`;
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </button>

                {/* Admin-only actions */}
                {isAdminUser && (
                  <>
                    <div className="border-t border-gray-200 my-1" />
                    
                    {/* Screen Access Control */}
                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Screen Access</div>
                    {isEnabled ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDisableUser();
                        }}
                        disabled={disabling}
                        className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        {disabling ? 'Restricting...' : 'Restrict Access'}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnableUser();
                        }}
                        disabled={enabling}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {enabling ? 'Restoring...' : 'Restore Access'}
                      </button>
                    )}

                    {/* Login Access Control */}
                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase mt-1">Login Access</div>
                    {!loginDisabled ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBlockLogin();
                        }}
                        disabled={disablingLogin}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        {disablingLogin ? 'Blocking...' : 'Block Login'}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnableLogin();
                        }}
                        disabled={enablingLogin}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {enablingLogin ? 'Enabling...' : 'Enable Login'}
                      </button>
                    )}

                    <div className="border-t border-gray-200 my-1" />

                    {/* Reset Password */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetPassword();
                      }}
                      disabled={resetting}
                      className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      {resetting ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Temporary Password Modal */}
      {showPasswordModal && temporaryPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Temporary Password Generated</h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setTemporaryPassword(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  A temporary password has been generated for <strong>{clientName}</strong>. 
                  Please share this securely with the client.
                </p>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
                  <p className="text-xs text-gray-500 mb-1">Temporary Password</p>
                  <p className="text-lg font-mono font-bold text-gray-900 break-all">
                    {temporaryPassword}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Important:</strong> The client must change this password upon their next login. 
                    This password will not be shown again.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleCopyPassword}
                  variant="outline"
                  className="flex-1"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </Button>
                <Button
                  onClick={handleSendEmail}
                  className="flex-1"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Client
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
